import { Switch, Tag } from "antd";
import { useState } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrashCan } from "@fortawesome/free-solid-svg-icons";

import {
  useGetBasicSettingsList,
  useUpdateBasicSettings,
} from "../../api/settings";
import styleScene from "./Scene.module.css";
import { useDeleteScene, useToggleSceneGuestAccess } from "../../api/scenes";
import { useGetLoggedInUser } from "../../api/loggedinUser";
import { isUberadmin } from "../../utils/permissions";
import { useDisclosure } from "../../hooks/useDisclosure";
import StyledModal from "../../Components/Modal/StyledModal";

const SCENE_TYPE_META = {
  unityScene: { label: "Unity Scene", color: "blue" },
  webUrl: { label: "Web URL", color: "green" },
  "360Tour": { label: "360 Tour", color: "purple" },
};

const getSceneTypeMeta = scene => {
  if (scene?.type && SCENE_TYPE_META[scene.type]) {
    return SCENE_TYPE_META[scene.type];
  }
  if (scene?.tour_360 || scene?.tour_url) {
    return SCENE_TYPE_META["360Tour"];
  }
  if (scene?.web_url) {
    return SCENE_TYPE_META.webUrl;
  }
  return SCENE_TYPE_META.unityScene;
};

export const useColumns = refetch => {
  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState(null);
  const [guestAccessById, setGuestAccessById] = useState({});
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { data: loggedInUserRes } = useGetLoggedInUser();
  const isAdmin = isUberadmin(loggedInUserRes?.data);
  const { mutateAsync } = useUpdateBasicSettings();
  const { data: settings } = useGetBasicSettingsList();
  const { mutateAsync: mutateAsyncDelete, isLoading: isLoadingDelete } =
    useDeleteScene();
  const { mutateAsync: mutateGuestAccess } = useToggleSceneGuestAccess();

  const handleChange = checkedId => checked => {
    if (checked) {
      const formData = new FormData();
      formData.append("default_scene", checkedId);
      mutateAsync(formData)
        .then(res => {
          if (res?.data) {
            refetch();
            toast.success(res?.data?.message);
          }
        })
        .catch(err => {
          toast.error(err?.message);
        });
    }
  };

  const handleDeleteScene = () => {
    mutateAsyncDelete(deleteId)
      .then(res => {
        if (res?.data) {
          toast.success(res?.data?.message);
          onClose();
          refetch();
        }
      })
      .catch(err => {
        toast.error(err?.response?.data?.error);
        onClose();
      });
  };

  const handleDelete = id => {
    onOpen();
    setDeleteId(id);
  };

  const handleGuestAccessToggle = (sceneId, nextValue) => {
    const previous = guestAccessById[sceneId];
    setGuestAccessById(prev => ({ ...prev, [sceneId]: nextValue }));
    mutateGuestAccess(sceneId)
      .then(res => {
        const confirmed = res?.data?.data?.guest_access;
        if (typeof confirmed === "boolean") {
          setGuestAccessById(prev => ({ ...prev, [sceneId]: confirmed }));
        }
      })
      .catch(err => {
        setGuestAccessById(prev => ({
          ...prev,
          [sceneId]: typeof previous === "boolean" ? previous : !nextValue,
        }));
        toast.error(err?.response?.data?.error || err?.message);
      });
  };

  const columns = [
    {
      title: "S.N",
      render: ({ sn }) => {
        return <div>{sn}</div>;
      },
    },
    {
      title: "Title",
      render: ({ id, title, public_id }) => {
        return (
          <Link to={`/scene/${public_id}`} className={styleScene.link}>
            {title}
          </Link>
        );
      },
    },

    // {
    //   title: "Username",
    //   render: ({ id }) => {
    //     const mockUsers = ["Admin User", "Tester", "John Doe", "Demo User"];
    //     return <div>{mockUsers[(id || 0) % 4]}</div>;
    //   },
    // },
    {
      title: "Scene Type",
      render: row => {
        const meta = getSceneTypeMeta(row);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Visibility",
      render: ({ visibility }) => {
        const value = visibility || "Public";
        return (
          <Tag color={value === "Private" ? "orange" : "green"}>{value}</Tag>
        );
      },
    },
    {
      title: "Order",
      render: ({ order }) => {
        return <div>{order ?? 0}</div>;
      },
    },
    {
      title: "Status",
      render: ({ status }) => {
        const currentStatus = status || "PUBLISHED";
        const isDraft = currentStatus.toUpperCase() === "DRAFT";
        return (
          <Tag color={isDraft ? "orange" : "green"}>
            {isDraft ? "Draft" : "Published"}
          </Tag>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            title: "Guest Access",
            render: row => {
              const guestAccess =
                guestAccessById[row.id] ?? Boolean(row.guest_access);
              return (
                <Switch
                  checked={guestAccess}
                  onChange={checked => handleGuestAccessToggle(row.id, checked)}
                />
              );
            },
          },
        ]
      : []),
    {
      title: "Image",
      render: ({ image }) => {
        return (
          <div style={{ display: "flex", justifyContent: "left" }}>
            <img
              style={{ width: "80px", height: "auto" }}
              src={`${image}`}
              alt="scene"
            />
          </div>
        );
      },
    },
    {
      title: "Action",
      render: row => {
        return (
          <div className="flex gap-2">

            <button
              onClick={() =>
                navigate(`/web-twinprocms/scene/${row?.id}/update/${row?.public_id}`, {
                  state: { item: row },
                })
              }
              className={styleScene["modify-scene-btn"]}
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
            <button
              className={styleScene["delete-scene-btn"]}
              onClick={() => handleDelete(row?.id)}
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
            <StyledModal
              isOpen={isOpen}
              onClose={onClose}
              onConfirm={handleDeleteScene}
              loading={isLoadingDelete}
            />
          </div>
        );
      },
    },

    {
      ...(settings?.immersive_experience && {
        title: "Set Homepage",
        render: ({ id, immersive_default_scene }) => {
          return (
            <Switch
              checked={immersive_default_scene}
              onChange={handleChange(id)}
            />
          );
        },
      }),
    },
  ];
  return { columns };
};
